import { type ActivityRequest, type User } from "../types";

export function applyOptimisticLeave(
  activity: ActivityRequest,
  userId: string,
): ActivityRequest {
  return {
    ...activity,
    participants: activity.participants.filter(
      (participant) => participant.id !== userId,
    ),
    participantIds: (activity.participantIds ?? []).filter(
      (participantId) => participantId !== userId,
    ),
    currentSize: Math.max(
      0,
      (activity.currentSize || activity.participants.length) - 1,
    ),
    isFull: false,
  };
}

export function applyOptimisticInviteUsers(
  activity: ActivityRequest,
  userIds: string[],
): ActivityRequest {
  return {
    ...activity,
    pendingInvites: Array.from(
      new Set([...(activity.pendingInvites ?? []), ...userIds]),
    ),
  };
}

export function applyOptimisticCancelInvite(
  activity: ActivityRequest,
  userId: string,
): ActivityRequest {
  return {
    ...activity,
    pendingInvites: (activity.pendingInvites ?? []).filter(
      (pendingUserId) => pendingUserId !== userId,
    ),
  };
}

export function applyOptimisticInviteResponse(
  activity: ActivityRequest,
  currentUser: User,
  accept: boolean,
): ActivityRequest {
  const alreadyParticipant = activity.participants.some(
    (participant) => participant.id === currentUser.id,
  );
  const nextSize =
    accept && !alreadyParticipant
      ? (activity.currentSize || activity.participants.length) + 1
      : activity.currentSize;

  return {
    ...activity,
    pendingInvites: (activity.pendingInvites ?? []).filter(
      (pendingUserId) => pendingUserId !== currentUser.id,
    ),
    participants:
      accept && !alreadyParticipant
        ? [...activity.participants, currentUser]
        : activity.participants,
    participantIds:
      accept && !alreadyParticipant
        ? [...(activity.participantIds ?? []), currentUser.id]
        : activity.participantIds,
    currentSize: nextSize,
    isFull:
      accept && !alreadyParticipant
        ? nextSize >= activity.groupSize
        : activity.isFull,
  };
}

export function applyOptimisticApproveRejoin(
  activity: ActivityRequest,
  userId: string,
): ActivityRequest {
  return {
    ...activity,
    pendingRejoinRequests: (activity.pendingRejoinRequests ?? []).filter(
      (pendingUserId) => pendingUserId !== userId,
    ),
    kickedParticipantIds: (activity.kickedParticipantIds ?? []).filter(
      (participantId) => participantId !== userId,
    ),
  };
}

export function applyOptimisticDeclineRejoin(
  activity: ActivityRequest,
  userId: string,
): ActivityRequest {
  return {
    ...activity,
    pendingRejoinRequests: (activity.pendingRejoinRequests ?? []).filter(
      (pendingUserId) => pendingUserId !== userId,
    ),
  };
}

export function applyOptimisticParticipantKick(
  activity: ActivityRequest,
  participantId: string,
): ActivityRequest {
  return applyOptimisticLeave(activity, participantId);
}